import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import { tipoIcon, NIVEIS_ATENUACAO } from '../../utils/fitTestOpcoes';
import { ordinal } from '../../utils/ciclo';
import { todayIso } from '../../utils/date';
import { updateFitTest, confirmarFitTest, recusarFitTest, standbyFitTest } from '../../api/fitTests';
import { getEquipamentos } from '../../api/equipamentos';
import { useAuth } from '../../contexts/AuthContext';

const DIAS_PT  = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
const MESES_PT = ['janeiro','fevereiro','março','abril','maio','junho',
                  'julho','agosto','setembro','outubro','novembro','dezembro'];

const TIPOS_RECUSA = [
  'Data indisponível','Feriado / ponto facultativo','Impossibilidade operacional',
  'Capacidade excedida','Equipamento em manutenção','Outro',
];

function fmtDataLonga(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const dt = new Date(Number(y), Number(m)-1, Number(d));
  return `${DIAS_PT[dt.getDay()]}, ${d} de ${MESES_PT[dt.getMonth()]} de ${y}`;
}

export default function AgendaDetalhe() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { isFenix, isAdmin } = useAuth();
  const { data, empresa } = location.state || {};

  const [tests, setTests] = useState(empresa?.tests || []);
  const [equipamentos, setEquipamentos] = useState([]);
  const [saving, setSaving] = useState(false);

  // Estado de qual ação está aberta por teste
  const [activeAction, setActiveAction] = useState(null); // { id, type: 'resultado'|'confirmar'|'recusar' }

  // Formulários
  const [resultForm, setResultForm]   = useState({ resultado: 'APROVADO', data_realizada: todayIso(), metodo: '', duracao_teste: '', exposicao_db: '', nap_resultante: '', nivel_atenuacao: '' });
  const [confirmarEquip, setConfirmarEquip] = useState('');
  const [recusaForm, setRecusaForm]   = useState({ tipo_recusa: TIPOS_RECUSA[0], descricao_recusa: '', data_sugerida: '' });

  useEffect(() => {
    if (isFenix) getEquipamentos().then(setEquipamentos);
  }, [isFenix]);

  const closeAll = () => { setActiveAction(null); setConfirmarEquip(''); };

  const openAction = (t, type) => {
    if (activeAction?.id === t.id_fit_test && activeAction?.type === type) {
      closeAll();
      return;
    }
    closeAll();
    setActiveAction({ id: t.id_fit_test, type });
    if (type === 'resultado') {
      setResultForm({
        resultado: t.resultado || 'APROVADO',
        data_realizada: todayIso(),
        metodo: t.metodo || '',
        duracao_teste: t.duracao_teste || '',
        exposicao_db: t.exposicao_db || '',
        nap_resultante: t.nap_resultante || '',
        nivel_atenuacao: t.nivel_atenuacao || '',
      });
    }
    if (type === 'recusar') {
      setRecusaForm({ tipo_recusa: TIPOS_RECUSA[0], descricao_recusa: '', data_sugerida: '' });
    }
    if (type === 'confirmar') {
      setConfirmarEquip(t.id_equipamento || '');
    }
  };

  const patchTests = (id, patch) =>
    setTests(prev => prev.map(t => t.id_fit_test === id ? { ...t, ...patch } : t));

  // ── Lançar resultado ─────────────────────────────────────────────────────────
  const handleResult = async (e) => {
    e.preventDefault();
    const testAtual = tests.find(t => t.id_fit_test === activeAction.id);
    setSaving(true);
    const payload = {
      resultado: resultForm.resultado,
      data_realizada: resultForm.data_realizada,
      metodo: resultForm.metodo || null,
      duracao_teste: resultForm.duracao_teste || null,
    };
    if (testAtual?.tipo_teste === 'auditivo') {
      payload.exposicao_db = resultForm.exposicao_db || null;
      payload.nap_resultante = resultForm.nap_resultante || null;
      payload.nivel_atenuacao = resultForm.nivel_atenuacao || null;
    }
    try {
      await updateFitTest(activeAction.id, payload);
      patchTests(activeAction.id, { resultado: resultForm.resultado, data_realizada: resultForm.data_realizada });
      closeAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao lançar resultado');
    } finally { setSaving(false); }
  };

  // ── Confirmar ────────────────────────────────────────────────────────────────
  const handleConfirmar = async (t) => {
    const equipId = confirmarEquip || null;
    if (!t.id_equipamento && !equipId) {
      alert('Selecione um equipamento para confirmar.');
      return;
    }
    setSaving(true);
    try {
      await confirmarFitTest(t.id_fit_test, equipId);
      patchTests(t.id_fit_test, { situacao: 'confirmado', id_equipamento: equipId || t.id_equipamento });
      closeAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao confirmar');
    } finally { setSaving(false); }
  };

  // ── Recusar ──────────────────────────────────────────────────────────────────
  const handleRecusar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await recusarFitTest(activeAction.id, { ...recusaForm, data_sugerida: recusaForm.data_sugerida || null });
      patchTests(activeAction.id, { situacao: 'recusado' });
      closeAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao recusar');
    } finally { setSaving(false); }
  };

  // ── Standby ──────────────────────────────────────────────────────────────────
  const handleStandby = async (id) => {
    setSaving(true);
    try {
      await standbyFitTest(id);
      patchTests(id, { situacao: 'standby' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro');
    } finally { setSaving(false); }
  };

  // ── Reativar (standby → confirmar) ───────────────────────────────────────────
  const handleReativar = async (t) => {
    setSaving(true);
    try {
      await confirmarFitTest(t.id_fit_test, null);
      patchTests(t.id_fit_test, { situacao: 'confirmado' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao reativar');
    } finally { setSaving(false); }
  };

  if (!data || !empresa || !tests) {
    return (
      <div className="p-4 text-center text-muted">
        <p>Dados não encontrados.</p>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/agenda')}>
          Voltar para Agenda
        </button>
      </div>
    );
  }

  const grupos = tests.reduce((acc, t) => {
    const k = `${t.tipo_teste}/${t.grupo_teste || ''}`;
    if (!acc[k]) acc[k] = { tipo: t.tipo_teste, grupo: t.grupo_teste, tests: [] };
    acc[k].tests.push(t);
    return acc;
  }, {});

  const colSpan = isFenix ? 8 : 7;

  return (
    <>
      <button className="btn btn-sm btn-link text-muted mb-3 ps-0"
        onClick={() => navigate('/agenda')}>
        <i className="bi bi-arrow-left me-1"></i>Voltar para Agenda
      </button>

      <PageHeader title={empresa.nome} subtitle={fmtDataLonga(data)} />

      <div className="d-flex gap-2 align-items-center mb-4 flex-wrap">
        <span className="badge rounded-pill fs-6"
          style={{ background: 'var(--main-color)', color: '#fff' }}>
          {tests.length} teste(s)
        </span>
        {Object.values(grupos).map(({ tipo, grupo, tests: ts }) => (
          <span key={`${tipo}${grupo}`} className="badge bg-secondary">
            <i className={`bi ${tipoIcon(tipo)} me-1`}></i>
            {grupo || tipo} × {ts.length}
          </span>
        ))}
      </div>

      <div className="card-datafit">
        <div className="table-responsive">
          <table className="table table-datafit mb-0">
            <thead>
              <tr>
                <th>Ciclo</th>
                <th>Tipo / Grupo</th>
                <th>Funcionário</th>
                <th>Equipamento</th>
                <th>Local</th>
                <th>Situação</th>
                <th>Resultado</th>
                {isFenix && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tests.length === 0 ? (
                <tr><td colSpan={colSpan} className="text-center text-muted py-4">Nenhum teste</td></tr>
              ) : tests.map(t => (
                <>
                  <tr key={t.id_fit_test}>
                    <td>
                      <span className="badge rounded-pill"
                        style={{ background: 'var(--main-color)', color: '#fff' }}>
                        {ordinal(t.numero_ciclo)}
                      </span>
                    </td>
                    <td>
                      <div><i className={`bi ${tipoIcon(t.tipo_teste)} me-1`}></i>{t.tipo_teste}</div>
                      {t.grupo_teste && <div className="small text-muted">{t.grupo_teste}</div>}
                    </td>
                    <td>
                      <div className="fw-500">{t.funcionario_empresa?.funcionario?.nome || '—'}</div>
                      <div className="small text-muted">{t.setor || t.funcionario_empresa?.cargo || '—'}</div>
                    </td>
                    <td>
                      <div>{t.equipamento?.ca || '—'}</div>
                      <div className="small text-muted">{t.equipamento?.tipo_mascara}</div>
                    </td>
                    <td className="small text-muted">
                      {t.local?.nome
                        ? <span><i className="bi bi-geo-alt me-1"></i>{t.local.nome}</span>
                        : t.funcionario_empresa?.embarcacao || '—'}
                    </td>
                    <td><StatusBadge status={t.situacao} /></td>
                    <td>
                      {t.resultado
                        ? <StatusBadge status={t.resultado.toLowerCase()} />
                        : <span className="text-muted small">—</span>}
                    </td>

                    {isFenix && (
                      <td>
                        <div className="d-flex gap-1">
                          {/* Confirmar — em_analise */}
                          {t.situacao === 'em_analise' && (
                            <button className="btn btn-sm btn-success" title="Confirmar agendamento"
                              onClick={() => openAction(t, 'confirmar')}>
                              <i className="bi bi-check-lg"></i>
                            </button>
                          )}
                          {/* Recusar — em_analise */}
                          {t.situacao === 'em_analise' && (
                            <button className="btn btn-sm btn-danger" title="Recusar agendamento"
                              onClick={() => openAction(t, 'recusar')}>
                              <i className="bi bi-x-lg"></i>
                            </button>
                          )}
                          {/* Resultado — confirmado */}
                          {t.situacao === 'confirmado' && (isAdmin || !t.data_realizada) && (
                            <button className="btn btn-sm btn-outline-success" title="Lançar resultado"
                              onClick={() => openAction(t, 'resultado')}>
                              <i className="bi bi-patch-check-fill"></i>
                            </button>
                          )}
                          {/* Standby — confirmado sem resultado */}
                          {t.situacao === 'confirmado' && !t.data_realizada && (
                            <button className="btn btn-sm btn-outline-secondary" title="Colocar em standby"
                              disabled={saving}
                              onClick={() => handleStandby(t.id_fit_test)}>
                              <i className="bi bi-pause-circle"></i>
                            </button>
                          )}
                          {/* Reativar — standby */}
                          {t.situacao === 'standby' && (
                            <button className="btn btn-sm btn-outline-success" title="Reativar"
                              disabled={saving}
                              onClick={() => handleReativar(t)}>
                              <i className="bi bi-play-circle"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>

                  {/* ── Form: Confirmar ─────────────────────────────────────── */}
                  {activeAction?.id === t.id_fit_test && activeAction?.type === 'confirmar' && (
                    <tr key={`confirmar-${t.id_fit_test}`} className="table-success">
                      <td colSpan={colSpan} className="p-3">
                        <div className="d-flex align-items-end gap-3 flex-wrap">
                          {!t.id_equipamento && (
                            <div>
                              <label className="form-label small mb-1 fw-500">
                                <i className="bi bi-box-seam me-1"></i>Equipamento
                              </label>
                              <select className="form-select form-select-sm" style={{ width: 300 }}
                                value={confirmarEquip}
                                onChange={e => setConfirmarEquip(e.target.value)}>
                                <option value="">Selecione...</option>
                                {equipamentos
                                  .filter(eq => !eq.tipo_teste || eq.tipo_teste === t.tipo_teste)
                                  .map(eq => (
                                    <option key={eq.id_equipamento} value={eq.id_equipamento}>
                                      {eq.ca} — {eq.tipo_mascara}{eq.fabricante ? ` (${eq.fabricante})` : ''}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          )}
                          {t.id_equipamento && (
                            <div className="small text-muted">
                              <i className="bi bi-box-seam me-1"></i>
                              Equipamento: <strong>{t.equipamento?.ca || t.id_equipamento}</strong>
                            </div>
                          )}
                          <button className="btn btn-sm btn-success"
                            disabled={saving || (!t.id_equipamento && !confirmarEquip)}
                            onClick={() => handleConfirmar(t)}>
                            {saving ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1"></i>}
                            Confirmar agendamento
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={closeAll}>Cancelar</button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* ── Form: Recusar ───────────────────────────────────────── */}
                  {activeAction?.id === t.id_fit_test && activeAction?.type === 'recusar' && (
                    <tr key={`recusar-${t.id_fit_test}`} className="table-danger">
                      <td colSpan={colSpan} className="p-3">
                        <form onSubmit={handleRecusar} className="row g-2">
                          <div className="col-md-4">
                            <label className="form-label small mb-1 fw-500">Motivo *</label>
                            <select className="form-select form-select-sm" required
                              value={recusaForm.tipo_recusa}
                              onChange={e => setRecusaForm(f => ({ ...f, tipo_recusa: e.target.value }))}>
                              {TIPOS_RECUSA.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="col-md-5">
                            <label className="form-label small mb-1 fw-500">Descrição (opcional)</label>
                            <input type="text" className="form-control form-control-sm"
                              value={recusaForm.descricao_recusa}
                              onChange={e => setRecusaForm(f => ({ ...f, descricao_recusa: e.target.value }))} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small mb-1 fw-500">Data sugerida (opcional)</label>
                            <input type="date" className="form-control form-control-sm"
                              value={recusaForm.data_sugerida}
                              onChange={e => setRecusaForm(f => ({ ...f, data_sugerida: e.target.value }))} />
                          </div>
                          <div className="col-12 d-flex gap-2">
                            <button type="submit" className="btn btn-sm btn-danger" disabled={saving}>
                              {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                              Recusar
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeAll}>Cancelar</button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}

                  {/* ── Form: Resultado ─────────────────────────────────────── */}
                  {activeAction?.id === t.id_fit_test && activeAction?.type === 'resultado' && (
                    <tr key={`res-${t.id_fit_test}`} className="table-success">
                      <td colSpan={colSpan} className="p-3">
                        <form onSubmit={handleResult} className="row g-2 align-items-end">
                          <div className="col-auto">
                            <label className="form-label small mb-1 fw-500">Resultado</label>
                            <select className="form-select form-select-sm" style={{ width: 150 }}
                              value={resultForm.resultado}
                              onChange={e => setResultForm(f => ({ ...f, resultado: e.target.value }))}>
                              <option value="APROVADO">Aprovado</option>
                              <option value="REPROVADO">Reprovado</option>
                              <option value="REFAZER">Refazer</option>
                            </select>
                          </div>
                          <div className="col-auto">
                            <label className="form-label small mb-1 fw-500">Data realizada</label>
                            <input type="date" className="form-control form-control-sm" style={{ width: 160 }}
                              value={resultForm.data_realizada}
                              min={isAdmin ? undefined : todayIso()}
                              onChange={e => setResultForm(f => ({ ...f, data_realizada: e.target.value }))} />
                          </div>
                          <div className="col-auto">
                            <label className="form-label small mb-1 fw-500">Método</label>
                            <input type="text" className="form-control form-control-sm" style={{ width: 140 }}
                              placeholder="Ex: ABNT NR15"
                              value={resultForm.metodo}
                              onChange={e => setResultForm(f => ({ ...f, metodo: e.target.value }))} />
                          </div>
                          <div className="col-auto">
                            <label className="form-label small mb-1 fw-500">Duração</label>
                            <input type="text" className="form-control form-control-sm" style={{ width: 100 }}
                              placeholder="Ex: 45min"
                              value={resultForm.duracao_teste}
                              onChange={e => setResultForm(f => ({ ...f, duracao_teste: e.target.value }))} />
                          </div>

                          {t.tipo_teste === 'auditivo' && (
                            <>
                              <div className="col-auto">
                                <label className="form-label small mb-1 fw-500">Exposição dB(A)</label>
                                <input type="number" step="0.1" className="form-control form-control-sm" style={{ width: 110 }}
                                  placeholder="Ex: 78.5"
                                  value={resultForm.exposicao_db}
                                  onChange={e => setResultForm(f => ({ ...f, exposicao_db: e.target.value }))} />
                              </div>
                              <div className="col-auto">
                                <label className="form-label small mb-1 fw-500">NAP resultante</label>
                                <input type="number" step="0.1" className="form-control form-control-sm" style={{ width: 110 }}
                                  placeholder="Ex: 23.2"
                                  value={resultForm.nap_resultante}
                                  onChange={e => setResultForm(f => ({ ...f, nap_resultante: e.target.value }))} />
                              </div>
                              <div className="col-auto">
                                <label className="form-label small mb-1 fw-500">Nível de atenuação</label>
                                <select className="form-select form-select-sm" style={{ width: 210 }}
                                  value={resultForm.nivel_atenuacao}
                                  onChange={e => setResultForm(f => ({ ...f, nivel_atenuacao: e.target.value }))}>
                                  <option value="">—</option>
                                  {NIVEIS_ATENUACAO.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                              </div>
                            </>
                          )}

                          <div className="col-auto d-flex gap-2">
                            <button type="submit" className="btn btn-sm btn-success" disabled={saving}>
                              {saving ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1"></i>}
                              Confirmar
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeAll}>
                              Cancelar
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
